import { useQuery } from "@tanstack/react-query";
import { queryCommonFlags } from "@/api/constants";
import { getAds, getAdsets, getCampaigns } from "@/modules/common/api/index.v2";
import { useState } from "react";
import { debounce } from "lodash";

const useCampaignsData = ({ currentPage }: { currentPage?: string } = {}) => {
  const [searchTerms, setSearchTerms] = useState({
    ads: "",
    adsets: "",
    campaigns: "",
  });

  const storedOrgName = localStorage.getItem(`selectedOrganizationName`);
  const HomeLocalStorageFilters = localStorage.getItem(
    `leadSearchFilters${storedOrgName}`
  );

  const parsedFilters = HomeLocalStorageFilters
    ? JSON.parse(HomeLocalStorageFilters)
    : {};
  const nextActionsLocalStorageFilters = localStorage.getItem(
    `nextActionsSearchFilters${storedOrgName}`
  );

  const parsedFiltersNextActions = nextActionsLocalStorageFilters
    ? JSON.parse(nextActionsLocalStorageFilters)
    : {};

  const path = window.location.pathname;

  const updateSearchTerm = (type: string, value: string) => {
    setSearchTerms((prev) => ({ ...prev, [type]: value }));
  };

  const handleSearchChange = (type: string) => {
    return debounce((_, value) => {
      updateSearchTerm(type, value);
    }, 300);
  };

  const getParams = (term: string) =>
    term.trim() !== ""
      ? { page: 1, pageSize: 50, name: term }
      : { page: 1, pageSize: 50 };

  const {
    data: CampaignsData,
    error: campaignsError,
    isLoading: campaignsLoading,
  } = useQuery({
    queryKey: ["getCampaigns", getParams(searchTerms.campaigns)],
    queryFn: async () => {
      const response = await getCampaigns(
        getParams(searchTerms.campaigns),
        currentPage === "Home"
          ? parsedFilters
          : path.includes("next-actions")
          ? parsedFiltersNextActions
          : {}
      );
      return response?.data?.results || [];
    },
    ...queryCommonFlags,
    staleTime: Infinity,
    select: (data) =>
      data?.map((campaign) => ({ id: campaign.uid, name: campaign.name })),
  });

  const {
    data: AdsData,
    error: adsError,
    isLoading: adsLoading,
  } = useQuery({
    queryKey: ["getAds", getParams(searchTerms.ads)],
    queryFn: async () => {
      const response = await getAds(getParams(searchTerms.ads));
      return response?.data?.results || [];
    },
    ...queryCommonFlags,
    staleTime: Infinity,
    select: (data) => data?.map((ad) => ({ id: ad.uid, name: ad.name })),
  });

  const {
    data: AdsetsData,
    error: adsetsError,
    isLoading: adsetsLoading,
  } = useQuery({
    queryKey: ["getAdsets", getParams(searchTerms.adsets)],
    queryFn: async () => {
      const response = await getAdsets(
        getParams(searchTerms.adsets),
        currentPage === "Home"
          ? parsedFilters
          : path.includes("next-actions")
          ? parsedFiltersNextActions
          : {}
      );
      return response?.data?.results || [];
    },
    ...queryCommonFlags,
    staleTime: Infinity,
    select: (data) =>
      data?.map((adset) => ({ id: adset.uid, name: adset.name })),
  });

  return {
    Campaigns: {
      CampaignsData,
      campaignsError,
      campaignsLoading,
      campaignsSearchTerm: searchTerms.campaigns,
      setCampaignsSearchTerm: (term: string) =>
        updateSearchTerm("campaigns", term),
      handleSearchChange: handleSearchChange("campaigns"),
    },
    Ads: {
      AdsData,
      adsError,
      adsLoading,
      adsSearchTerm: searchTerms.ads,
      setAdsSearchTerm: (term: string) => updateSearchTerm("ads", term),
      handleSearchChange: handleSearchChange("ads"),
    },
    Adsets: {
      AdsetsData,
      adsetsError,
      adsetsLoading,
      adsetsSearchTerm: searchTerms.adsets,
      setAdsetsSearchTerm: (term: string) => updateSearchTerm("adsets", term),
      handleSearchChange: handleSearchChange("adsets"),
    },
  };
};

export default useCampaignsData;
